'use client';

import {
  Building2, MapPin, FileText, BarChart3, CheckCircle, Eye,
  ArrowRight, Layers, Shield, Clock, Users, Brain,
  Database, MessageSquare, TrendingUp, Zap
} from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import { getThemeStyles } from '@/lib/themeStyles';

/* ═══════════════════════════════════════════════
   A프로젝트 실증 케이스 스터디
   "데이터 투명성 성공 사례" 패키징
   ═══════════════════════════════════════════════ */

const PROJECT_FACTS = [
  { label: '프로젝트명', value: 'A프로젝트 인테리어 실시설계', icon: Building2 },
  { label: '위치', value: '수도권', icon: MapPin },
  { label: '공사비', value: '₩12.85억', icon: BarChart3 },
  { label: '연면적', value: '1,148.8㎡ (16개 공간)', icon: Layers },
  { label: '설계 도면', value: '6차 인테리어 실시설계 (30장 투시도)', icon: FileText },
  { label: '실증 기간', value: '2025.06 ~ 2026.03', icon: Clock },
];

const SPACES = [
  { name: '북카페로비', area: '145.6㎡' },
  { name: '컨퍼런스홀', area: '185.2㎡' },
  { name: '스마트오피스', area: '126.8㎡' },
  { name: '다목적실', area: '98.5㎡' },
  { name: '체력단련실', area: '95.6㎡' },
  { name: '복도', area: '186.4㎡' },
  { name: '홀', area: '68.2㎡' },
  { name: '휴게공간', area: '55.3㎡' },
  { name: '교육장실', area: '42.8㎡' },
  { name: '중회의실', area: '35.2㎡' },
  { name: '화장실', area: '32.6㎡' },
  { name: '비서실', area: '28.6㎡' },
  { name: '안내실', area: '22.4㎡' },
  { name: '소회의실', area: '18.5㎡' },
  { name: '마더스룸', area: '12.8㎡' },
  { name: '대회의실', area: '72.4㎡' },
];

const TRANSPARENCY_RESULTS = [
  {
    metric: '설계 변경 누락',
    before: '평균 12% 누락',
    after: '0건 누락',
    desc: 'Agent Team의 도면↔내역서 크로스 분석으로 모든 변경 사항 즉시 감지',
    icon: Eye
  },
  {
    metric: '견적 정합성',
    before: '도면·내역서 수작업 대조 (3~5일)',
    after: '30초 이내 자동 검증',
    desc: 'BOQ·CAD 에이전트 병렬 분석으로 불일치 즉시 식별',
    icon: Shield
  },
  {
    metric: '의사결정 속도',
    before: '보고서 작성·검토 2~3주',
    after: '실시간 RAG 답변 (0.8초)',
    desc: 'Neo4j 캐시 히트 시 5.3초→0.8초, 6.3배 응답속도 개선',
    icon: Zap
  },
  {
    metric: '데이터 접근성',
    before: 'NAS 폴더 수동 탐색',
    after: '자연어 질의 → AI 답변',
    desc: '20개 폴더 227개 파일 + Notion 48개 섹션 → 통합 RAG 검색',
    icon: Brain
  },
];

const DATA_PIPELINE = [
  { step: 'Phase 1', label: '프로젝트 인덱싱', desc: 'NAS 203개 프로젝트 → Neo4j ProjectIndex', count: '93', color: '#6366f1' },
  { step: 'Phase 2', label: '문서 텍스트 추출', desc: 'PDF/Excel/HWP → Neo4j DocumentContext', count: '38', color: '#06b6d4' },
  { step: 'Phase 3', label: 'FAQ 사전 캐싱', desc: 'Gemini AI → Neo4j SemanticCache', count: '33', color: '#f59e0b' },
];

export default function PTKCaseStudy() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const t = getThemeStyles(isDark);

  return (
    <main style={{ minHeight: '100vh', paddingTop: 64, background: t.pageBg2, color: t.textWhite, fontFamily: "'Inter','Pretendard',system-ui,sans-serif" }}>
      {/* Header */}
      <header style={{ borderBottom: `1px solid ${t.borderLight}`, background: t.headerBg, backdropFilter: 'blur(20px)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: 'linear-gradient(135deg, #22c55e, #06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 style={{ width: 20, height: 20, color: '#fff' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 14, fontWeight: 800, margin: 0 }}>Case Study</h1>
              <p style={{ fontSize: 10, color: t.textMuted, margin: 0 }}>A프로젝트 · 데이터 투명성 실증</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <a href="/ir" style={{ fontSize: 12, color: t.accentIndigo, textDecoration: 'none' }}>IR →</a>
            <a href="/ptk" style={{ fontSize: 12, color: isDark ? '#22d3ee' : '#0891b2', textDecoration: 'none' }}>대시보드 →</a>
            <a href="/" style={{ fontSize: 12, color: t.textMuted, textDecoration: 'none' }}>← 홈</a>
          </div>
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '48px 24px' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 9999, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', marginBottom: 24 }}>
            <CheckCircle style={{ width: 14, height: 14, color: '#10b981' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: '#10b981' }}>실증 완료</span>
          </div>
          <h2 style={{ fontSize: 42, fontWeight: 900, lineHeight: 1.2, marginBottom: 16 }}>
            데이터 투명성으로<br />
            <span style={{ background: 'linear-gradient(to right, #22c55e, #06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>설계 변경 누락 0건</span> 달성
          </h2>
          <p style={{ fontSize: 16, color: t.textSecondary, maxWidth: 600, margin: '0 auto' }}>
            ₩12.85억 규모 공공 인테리어 프로젝트에서 SnapCodex AI 파이프라인을 적용,<br />
            도면↔내역서↔시방서 실시간 크로스 검증으로 설계 품질을 획기적으로 개선했습니다.
          </p>
        </div>

        {/* 프로젝트 개요 */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 12, marginBottom: 48 }}>
          {PROJECT_FACTS.map((fact) => {
            const Icon = fact.icon;
            return (
              <div key={fact.label} style={{ padding: 16, borderRadius: 12, background: t.cardBg, border: `1px solid ${t.borderLight}`, textAlign: 'center' }}>
                <Icon style={{ width: 16, height: 16, color: isDark ? '#22d3ee' : '#0891b2', margin: '0 auto 8px' }} />
                <div style={{ fontSize: 10, color: t.textMuted, marginBottom: 4 }}>{fact.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{fact.value}</div>
              </div>
            );
          })}
        </div>

        {/* Before → After */}
        <div style={{ marginBottom: 64 }}>
          <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp style={{ width: 24, height: 24, color: '#22c55e' }} />
            데이터 투명성 성과 — Before vs After
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {TRANSPARENCY_RESULTS.map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.metric} style={{ borderRadius: 16, border: '1px solid rgba(16,185,129,0.15)', background: 'rgba(16,185,129,0.03)', padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <Icon style={{ width: 20, height: 20, color: '#22c55e' }} />
                    <span style={{ fontSize: 16, fontWeight: 800 }}>{r.metric}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600, marginBottom: 4 }}>BEFORE</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#fca5a5' : '#dc2626' }}>{r.before}</div>
                    </div>
                    <ArrowRight style={{ width: 20, height: 20, color: '#22c55e' }} />
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)', textAlign: 'center' }}>
                      <div style={{ fontSize: 10, color: '#10b981', fontWeight: 600, marginBottom: 4 }}>AFTER</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: isDark ? '#6ee7b7' : '#16a34a' }}>{r.after}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: t.textMuted }}>{r.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 데이터 파이프라인 */}
        <div style={{ marginBottom: 64 }}>
          <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Database style={{ width: 24, height: 24, color: '#06b6d4' }} />
            캐싱 파이프라인 — SnapFoundry 엔진
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {DATA_PIPELINE.map((p) => (
              <div key={p.step} style={{ borderRadius: 16, border: `1px solid ${p.color}30`, background: `${p.color}08`, padding: 24, textAlign: 'center' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: p.color, marginBottom: 4 }}>{p.step}</div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>{p.label}</div>
                <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 12 }}>{p.desc}</p>
                <div style={{ fontSize: 36, fontWeight: 900, color: p.color }}>{p.count}</div>
                <div style={{ fontSize: 10, color: t.textDim }}>Neo4j 노드</div>
              </div>
            ))}
          </div>
        </div>

        {/* 공간 구성 */}
        <div style={{ marginBottom: 64 }}>
          <h3 style={{ fontSize: 24, fontWeight: 800, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers style={{ width: 24, height: 24, color: '#f59e0b' }} />
            16개 공간 구성 (총 1,148.8㎡)
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {SPACES.sort((a, b) => parseFloat(b.area) - parseFloat(a.area)).map((s) => (
              <div key={s.name} style={{ padding: '12px 16px', borderRadius: 10, background: t.cardBg, border: `1px solid ${t.borderLight}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>{s.name}</span>
                <span style={{ fontSize: 11, color: t.textMuted, fontVariantNumeric: 'tabular-nums' }}>{s.area}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', padding: '48px 24px', borderRadius: 24, background: 'linear-gradient(135deg, rgba(16,185,129,0.06), rgba(6,182,212,0.06))', border: '1px solid rgba(16,185,129,0.15)' }}>
          <h3 style={{ fontSize: 28, fontWeight: 900, marginBottom: 16 }}>
            우리 프로젝트에도 적용하고 싶으시다면
          </h3>
          <p style={{ fontSize: 14, color: t.textSecondary, marginBottom: 24 }}>
            SnapCodex 데이터 투명성 파이프라인으로 설계 품질과 비용 정합성을 혁신하세요.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
            <a href="/ir" style={{ padding: '12px 24px', borderRadius: 12, background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: '#fff', fontSize: 14, fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
              IR 페이지 <ArrowRight style={{ width: 16, height: 16 }} />
            </a>
            <a href="/ptk" style={{ padding: '12px 24px', borderRadius: 12, background: t.ghostBg, border: `1px solid ${t.ghostBorder}`, color: t.textSecondary, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
              실증 대시보드
            </a>
          </div>
        </div>
      </div>

      <footer style={{ padding: 24, textAlign: 'center', fontSize: 12, color: t.textDim, borderTop: `1px solid ${t.borderLight}` }}>
        DesignInsight Inc. — 건축업의 온톨로지 선도기업 © 2026
      </footer>
    </main>
  );
}
